import { useState } from "react";
import type { ReactNode } from "react";
import type { GameSummary } from "@black-wall/shared";
import {
  useGames,
  useGameStatus,
  useGameCommand,
  useGameConfig,
  useSaveGameConfig,
  useGamePlaytime,
} from "../hooks/useGaming";

const BTN =
  "px-3 py-2 text-xs font-mono uppercase tracking-wider border bg-transparent transition-colors disabled:opacity-40";

function Panel({
  title,
  accent,
  children,
}: {
  title: string;
  accent?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="relative p-4 font-mono"
      style={{
        background: "var(--bw-glass-bg)",
        border: "1px solid var(--bw-glass-border)",
        boxShadow: "var(--bw-elevation-1)",
        clipPath:
          "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
      }}
    >
      <div
        className="mb-3 text-xs uppercase tracking-[0.2em]"
        style={{ color: accent ?? "var(--bw-accent)" }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span style={{ color: "var(--bw-muted)" }}>{label}</span>
      <br />
      {value}
    </div>
  );
}

export function GamingRoute() {
  const { data: games, isLoading } = useGames();

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <h1
        className="font-mono text-lg uppercase tracking-[0.2em]"
        style={{ color: "var(--bw-accent)", textShadow: "0 0 8px var(--bw-accent)" }}
      >
        Gaming
      </h1>

      {isLoading ? (
        <div className="font-mono text-sm" style={{ color: "var(--bw-muted)" }}>
          cargando registro de juegos…
        </div>
      ) : !games || games.length === 0 ? (
        <div className="font-mono text-sm" style={{ color: "var(--bw-muted)" }}>
          no hay juegos registrados
        </div>
      ) : (
        games.map((g) => <GameCard key={g.id} game={g} />)
      )}
    </div>
  );
}

function GameCard({ game }: { game: GameSummary }) {
  const { data } = useGameStatus(game.id);
  const cmd = useGameCommand(game.id);
  const [msg, setMsg] = useState("");
  const [showCfg, setShowCfg] = useState(false);

  const online = Boolean(data?.active);
  const players = data?.players ?? [];
  const res = data?.resources;
  const bk = data?.backups;
  const has = (a: string) => game.actions.includes(a);
  const run = (action: string, arg?: string) => cmd.mutate({ action, arg });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span
          className="inline-block h-3 w-3 rounded-full"
          style={{
            background: online ? "var(--bw-success)" : "var(--bw-danger)",
            boxShadow: online ? "0 0 10px var(--bw-success)" : "none",
          }}
        />
        <h2
          className="font-mono text-base uppercase tracking-[0.2em]"
          style={{ color: "var(--bw-fg)" }}
        >
          {game.label}
        </h2>
        <span className="font-mono text-xs" style={{ color: "var(--bw-muted)" }}>
          {online ? "online" : "offline"}
        </span>
      </div>

      <Panel title="Estado">
        <div
          className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3"
          style={{ color: "var(--bw-fg)" }}
        >
          <Field label="Nombre" value={data?.name ?? "—"} />
          <Field label="Jugadores" value={String(data?.players_online ?? 0)} />
          <Field
            label="Uptime"
            value={res?.uptime_s ? `${(res.uptime_s / 3600).toFixed(1)} h` : "—"}
          />
          <Field
            label="RAM"
            value={res?.rss_mb ? `${(res.rss_mb / 1024).toFixed(1)} GB` : "—"}
          />
          <Field label="CPU" value={res?.cpu_pct != null ? `${res.cpu_pct}%` : "—"} />
          <Field
            label="Backups"
            value={bk ? `${bk.count} · ${bk.last ?? "—"}` : "—"}
          />
        </div>
      </Panel>

      {players.length > 0 && (
        <Panel title={`Jugadores (${players.length})`}>
          <ul className="space-y-1 text-sm" style={{ color: "var(--bw-fg)" }}>
            {players.map((p) => (
              <li key={p.steamid || p.name} className="flex items-center justify-between gap-2">
                <span>{p.name}</span>
                <span className="flex gap-1">
                  {!p.steamid && (
                    <span className="text-[10px]" style={{ color: "var(--bw-muted)" }}>
                      sin RCON
                    </span>
                  )}
                  {has("kick") && p.steamid && (
                    <button
                      className={BTN}
                      style={{ borderColor: "var(--bw-warn)", color: "var(--bw-warn)" }}
                      onClick={() => run("kick", p.steamid)}
                    >
                      kick
                    </button>
                  )}
                  {has("ban") && p.steamid && (
                    <button
                      className={BTN}
                      style={{ borderColor: "var(--bw-danger)", color: "var(--bw-danger)" }}
                      onClick={() => {
                        if (confirm(`¿Banear a ${p.name}?`)) run("ban", p.steamid);
                      }}
                    >
                      ban
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Comandos rápidos">
        <div className="mb-3 flex flex-wrap gap-2">
          {has("save") && (
            <button className={BTN} style={{ borderColor: "var(--bw-accent)", color: "var(--bw-accent)" }} disabled={cmd.isPending} onClick={() => run("save")}>
              save
            </button>
          )}
          {has("backup") && (
            <button className={BTN} style={{ borderColor: "var(--bw-accent)", color: "var(--bw-accent)" }} disabled={cmd.isPending} onClick={() => run("backup")}>
              backup ya
            </button>
          )}
          {has("restart") && (
            <button className={BTN} style={{ borderColor: "var(--bw-warn)", color: "var(--bw-warn)" }} disabled={cmd.isPending} onClick={() => { if (confirm(`¿Reiniciar ${game.label}?`)) run("restart"); }}>
              restart
            </button>
          )}
          {game.extras?.map((x) => (
            <button
              key={x.label}
              className={BTN}
              style={{ borderColor: "var(--bw-accent-2, var(--bw-accent))", color: "var(--bw-accent-2, var(--bw-accent))" }}
              disabled={cmd.isPending}
              onClick={() => {
                if (x.confirm && !confirm(x.confirm)) return;
                run(x.action, x.arg);
              }}
            >
              {x.label}
            </button>
          ))}
          {game.configEditable && (
            <button className={BTN} style={{ borderColor: "var(--bw-muted)", color: "var(--bw-muted)" }} onClick={() => setShowCfg((v) => !v)}>
              {showCfg ? "cerrar config" : "editar config"}
            </button>
          )}
        </div>
        {has("broadcast") && (
          <div className="flex gap-2">
            <input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="broadcast a todos…"
              className="flex-1 border bg-transparent px-2 py-2 font-mono text-sm"
              style={{ borderColor: "var(--bw-border)", color: "var(--bw-fg)" }}
            />
            <button
              className={BTN}
              style={{ borderColor: "var(--bw-accent)", color: "var(--bw-accent)" }}
              disabled={!msg.trim() || cmd.isPending}
              onClick={() => {
                run("broadcast", msg.trim());
                setMsg("");
              }}
            >
              enviar
            </button>
          </div>
        )}
      </Panel>

      {/* command feedback — success AND error (the bug: errors were silent) */}
      {cmd.data && (
        <div
          className="p-2 font-mono text-xs"
          style={{
            color: cmd.data.error ? "var(--bw-danger)" : "var(--bw-success)",
            background: "var(--bw-glass-bg)",
            border: `1px solid ${cmd.data.error ? "var(--bw-danger)" : "var(--bw-glass-border)"}`,
          }}
        >
          &gt; {cmd.data.error ?? cmd.data.output ?? (cmd.data.ok ? "ok" : "sin salida")}
        </div>
      )}

      <PlaytimePanel gameId={game.id} />

      {showCfg && game.configEditable && <ConfigEditor gameId={game.id} label={game.label} />}
    </div>
  );
}

function formatHours(minutes: number): string {
  return (minutes / 60).toFixed(1) + " h";
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return "—";
  // "YYYY-MM-DD HH:MM:SS" (UTC) -> "DD/MM HH:MM"
  const [date, time] = iso.split(" ") as [string, string];
  const [y, m, d] = date.split("-") as [string, string, string];
  return `${d}/${m} ${time.slice(0, 5)} UTC`;
}

function PlaytimePanel({ gameId }: { gameId: string }) {
  const { data, isLoading } = useGamePlaytime(gameId);
  const players = data?.players ?? [];

  if (isLoading || players.length === 0) return null;

  return (
    <Panel title="Playtime histórico">
      <table className="w-full text-left text-sm" style={{ color: "var(--bw-fg)" }}>
        <thead>
          <tr style={{ color: "var(--bw-muted)" }} className="text-xs uppercase">
            <th className="pb-1 font-normal">Jugador</th>
            <th className="pb-1 font-normal">Sesiones</th>
            <th className="pb-1 font-normal">Tiempo total</th>
            <th className="pb-1 font-normal">Última vez</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr key={p.player}>
              <td className="py-0.5">
                {p.player}
                {p.ongoing && (
                  <span
                    className="ml-2 text-[10px]"
                    style={{ color: "var(--bw-success)" }}
                  >
                    ● en línea
                  </span>
                )}
              </td>
              <td className="py-0.5">{p.sessions}</td>
              <td className="py-0.5">{formatHours(p.totalMinutes)}</td>
              <td className="py-0.5" style={{ color: "var(--bw-muted)" }}>
                {p.ongoing ? "ahora" : formatLastSeen(p.lastSeenUtc)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data?.since && (
        <div className="mt-2 text-[10px]" style={{ color: "var(--bw-muted)" }}>
          datos desde {formatLastSeen(data.since)}
        </div>
      )}
    </Panel>
  );
}

function ConfigEditor({ gameId, label }: { gameId: string; label: string }) {
  const { data, isLoading } = useGameConfig(gameId, true);
  const save = useSaveGameConfig(gameId);
  const [draft, setDraft] = useState<string | null>(null);
  const [restart, setRestart] = useState(false);

  const content = draft ?? data?.content ?? "";
  const readError = data?.error;

  return (
    <Panel title={`Config · ${label}`} accent="var(--bw-warn)">
      {isLoading ? (
        <div className="text-sm" style={{ color: "var(--bw-muted)" }}>
          leyendo archivo…
        </div>
      ) : readError ? (
        <div className="text-sm" style={{ color: "var(--bw-danger)" }}>
          {readError}
        </div>
      ) : (
        <>
          <div className="mb-2 text-[10px]" style={{ color: "var(--bw-muted)" }}>
            {data?.path}
          </div>
          <textarea
            value={content}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            className="h-64 w-full resize-y border bg-transparent p-2 font-mono text-xs"
            style={{ borderColor: "var(--bw-border)", color: "var(--bw-fg)" }}
          />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs" style={{ color: "var(--bw-fg)" }}>
              <input
                type="checkbox"
                checked={restart}
                onChange={(e) => setRestart(e.target.checked)}
              />
              reiniciar el server al guardar
            </label>
            <button
              className={BTN}
              style={{ borderColor: "var(--bw-accent)", color: "var(--bw-accent)" }}
              disabled={save.isPending || draft === null}
              onClick={() => save.mutate({ content, restart })}
            >
              {save.isPending ? "guardando…" : "guardar"}
            </button>
            <span className="text-[10px]" style={{ color: "var(--bw-muted)" }}>
              se respalda el archivo actual antes de sobreescribir
            </span>
          </div>
          {save.data && (
            <div
              className="mt-2 p-2 text-xs"
              style={{ color: save.data.error ? "var(--bw-danger)" : "var(--bw-success)" }}
            >
              {save.data.error
                ? `error: ${save.data.error}`
                : `guardado${save.data.restarted ? " · server reiniciado" : ""}`}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
